import React, { useEffect, useState } from "react";
import { FilePenLine, History, LockKeyhole, Search, ShieldCheck } from "lucide-react";
import { Button } from "../../../components/new-components/ui/button";
import { Input } from "../../../components/new-components/ui/input";
import { Textarea } from "../../../components/new-components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/new-components/ui/sheet";
import {
  DaySheet,
  FIELD_REPLACEMENT_REASONS,
  ManifestPatch,
  ReplacementReason,
  ReplacementSide,
  RosterPatient,
  RosterLens,
  RosterOrder,
  TESTING_STATUS_LABEL,
  chartLockWords,
  mayRequestReplacement,
  patientName,
  providerApi,
  testingTitle,
  testingTone,
} from "../providerApi";
import type { TwlQResult } from "../providerApi";
import type { FieldWorkspaceProps } from "./contracts";
import { Notice, Section, StatusBadge, FieldSelect } from "./FieldPrimitives";
import { TagChipGroups, TwlQOutcomes, normTag } from "./ProviderTagChips";
import { PrivateNotes } from "./PrivateNotes";
import ClinicalEncountersPanel from "./clinical/ClinicalEncountersPanel";
// Remind PA on the workbench (ruling 7, 2026-10-27): the same button, dialog and server checks as the TWL pipeline.
import { RemindPaFor } from "./RemindPa";
import { ProviderResidentRecord } from "./ProviderResidentRecord";
import { RoomEditedMark, RoomField, RoomView } from "./RoomField";
import { StatusField } from "./StatusField";
import {
  BuildingMatch,
  WORKLIST_LENSES,
  residentSearchApi,
  worklistLabel,
} from "./manifestLenses";
import {
  REPLACEMENT_FILTERS,
  ReplacementChip,
  ReplacementFilter,
  ReplacementSection,
  matchesReplacementFilter,
  replacementOf,
  replacementRequestable,
} from "./ReplacementStatus";
// Facility rounds (2026-10-27, docs/provider/FACILITY_ROUNDS.md): seen marks, progress, Remaining / Seen / All.
import { AddedBackMark, RoundsContext, RoundsHeader, SeenChip, SeenLegend, WorkbenchSeen, useOfferSeen, useRounds } from "./FacilityRounds";
// "Can't find a resident?" (2026-10-28, docs/provider/CE_PULL.md).
import CePull from "./CePull";

/** One key per attempt: a retry after a lost answer is the same request, never a second one. */
function newRequestKey(): string {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return `fw-${c.randomUUID()}`;
  return `fw-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const NOTE_MAX = 1000;
const SIDES: { value: ReplacementSide; label: string }[] = [
  { value: "LEFT", label: "Left" },
  { value: "RIGHT", label: "Right" },
  { value: "BOTH", label: "Both" },
];

type RequestPhase = "closed" | "open" | "sending" | "sent";

/**
 * Ask the office for a hearing-aid replacement (2026-10-20). The request lands
 * on the Replacements desk in Manifest Review and the facility's Patient
 * Advocate and the PA managers are told. The provider says the side, the
 * reason and a note; how it is paid for is the office's call. Nothing is said
 * to have happened until the server confirms it.
 */
function ReplacementRequest({
  facilityId,
  patient,
  offline,
  onBusy,
  onDraft,
  onRecorded,
}: {
  facilityId: string;
  patient: RosterPatient;
  offline: boolean;
  onBusy: (busy: boolean) => void;
  onDraft: (draft: boolean) => void;
  /** The server recorded it: reload the day sheet, so the persisted replacement status replaces this message. */
  onRecorded?: () => void;
}) {
  const [phase, setPhase] = useState<RequestPhase>("closed");
  const [side, setSide] = useState<ReplacementSide | "">("");
  const [reason, setReason] = useState<ReplacementReason | "">("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [key, setKey] = useState(newRequestKey);
  const name = patientName(patient);
  const noteNeeded = reason === "OTHER" && !note.trim();
  const ready =
    side !== "" && reason !== "" && !noteNeeded && note.length <= NOTE_MAX;
  useEffect(() => {
    onDraft(phase === "open" && (side !== "" || reason !== "" || note !== ""));
  }, [phase, side, reason, note, onDraft]);
  // Unmounted (the reloaded day sheet says a request is open): no draft is left behind to confirm on close.
  useEffect(() => () => onDraft(false), [onDraft]);

  if (phase === "sent") {
    // Confirmed by the server. Shown only until the reloaded day sheet carries the persisted status, which then
    // takes this message's place in the Replacement section.
    return (
      <div className="fw-replacement" aria-label="Replacement request">
        <p role="status" className="fw-save-status">
          Replacement requested. It is on the office&rsquo;s Replacements desk,
          and the facility&rsquo;s Patient Advocate, the PA managers and the
          people who place replacements have been told.
          {onRecorded ? " Its status shows here as the PA team works on it." : ""}
        </p>
      </div>
    );
  }
  if (phase === "closed") {
    return (
      <div className="fw-replacement">
        <Button
          variant="outline"
          type="button"
          onClick={() => {
            setError("");
            setPhase("open");
          }}
          aria-label={`Request a replacement for ${name}`}
        >
          Request replacement
        </Button>
      </div>
    );
  }
  const sending = phase === "sending";
  return (
    <form
      className="fw-replacement"
      aria-label="Request a hearing-aid replacement"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!ready || sending || offline) return;
        setPhase("sending");
        onBusy(true);
        setError("");
        try {
          const r = await providerApi.requestReplacement(facilityId, patient.id, {
            side,
            reason_code: reason,
            note: note.trim() || null,
            idempotency_key: key,
          });
          if (r.success) {
            setPhase("sent");
            if (onRecorded) onRecorded();
            return;
          }
          if (r.status === undefined && !r.code) {
            // Never reached, or the answer was lost: keep the key, so trying again cannot record it twice.
            setError(
              "eMerge did not confirm the request. Nothing is shown as sent. Try again; trying again will not send it twice.",
            );
          } else {
            setError(
              r.message ||
                r.error ||
                "The request was not recorded. Tell the office.",
            );
            // A refusal recorded nothing; the next attempt is a new one.
            setKey(newRequestKey());
          }
          setPhase("open");
        } catch {
          setError(
            "eMerge did not confirm the request. Nothing is shown as sent. Try again; trying again will not send it twice.",
          );
          setPhase("open");
        } finally {
          onBusy(false);
        }
      }}
    >
      <h3 className="fw-replacement-title">Request a replacement</h3>
      <fieldset className="fw-choice" disabled={sending}>
        <legend>Which ear</legend>
        {SIDES.map((s) => (
          <label key={s.value}>
            <input
              type="radio"
              name={`fw-side-${patient.id}`}
              value={s.value}
              checked={side === s.value}
              onChange={() => setSide(s.value)}
            />
            {s.label}
          </label>
        ))}
      </fieldset>
      <div className="fw-field">
        <span>Why</span>
        <FieldSelect
          label="Why a replacement"
          value={reason}
          disabled={sending}
          onChange={(v) => setReason(v as ReplacementReason | "")}
          options={[
            { value: "", label: "Choose a reason" },
            ...FIELD_REPLACEMENT_REASONS,
          ]}
        />
      </div>
      <label htmlFor={`fw-rr-note-${patient.id}`}>
        Note {reason === "OTHER" ? "(required for Other)" : "(optional)"}
      </label>
      <Textarea
        id={`fw-rr-note-${patient.id}`}
        rows={4}
        value={note}
        maxLength={NOTE_MAX}
        readOnly={sending}
        onChange={(e) => setNote(e.target.value)}
        aria-describedby={`fw-rr-note-help-${patient.id}`}
      />
      <p id={`fw-rr-note-help-${patient.id}`} className="fw-muted">
        The office reads this note on the Replacements desk. It is not emailed.
      </p>
      {error && (
        <p role="alert" className="fw-error">
          {error}
        </p>
      )}
      <p role="status" className="fw-save-status">
        {sending ? "Sending the request to eMerge…" : ""}
      </p>
      <div className="fw-actions">
        <Button
          className="fw-primary"
          type="submit"
          disabled={!ready || sending || offline}
        >
          {sending ? "Sending…" : "Send replacement request"}
        </Button>
        <Button
          variant="outline"
          type="button"
          disabled={sending}
          onClick={() => {
            setPhase("closed");
            setError("");
          }}
        >
          Cancel
        </Button>
      </div>
      {offline && (
        <p className="fw-muted">You are offline. The request can be sent once you are connected.</p>
      )}
    </form>
  );
}

/** Clinical fields are never read from an unassigned row, even in a malformed payload. */
/** Two tag lists say the same thing (order kept: the server keeps the provider's order). */
const sameTags = (a: string[], b: string[]) =>
  a.length === b.length && a.every((t, i) => normTag(t) === normTag(b[i]));

function ResidentWorkbench({
  patient,
  sheet,
  onSave,
  onRefresh,
  onClose,
  offline,
}: {
  patient: RosterPatient;
  sheet: DaySheet;
  onSave: FieldWorkspaceProps["onSave"];
  onRefresh?: () => void;
  onClose: () => void;
  offline: boolean;
}) {
  const fields = sheet.provenance.writable
    ? sheet.provenance.writableFields || []
    : [];
  const canNotes = patient.care_assigned && fields.includes("notes");
  const canTags = patient.care_assigned && fields.includes("provider_tags");
  const vocabulary = sheet.provenance.providerTags;
  const [notes, setNotes] = useState(
    patient.care_assigned ? patient.notes || "" : "",
  );
  const [tags, setTags] = useState<string[]>(
    patient.care_assigned ? [...(patient.provider_tags || [])] : [],
  );
  const [baseline, setBaseline] = useState<{ notes: string; tags: string[] }>({ notes, tags });
  const [saving, setSaving] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [marking, setMarking] = useState(false);
  const [requestDraft, setRequestDraft] = useState(false);
  const busy = saving || requesting || marking;
  const setBusy = setSaving;
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const offerSeen = useOfferSeen();
  const canRequest = mayRequestReplacement(sheet, patient);
  const tagsDirty = canTags && !sameTags(tags, baseline.tags);
  const manifestDirty =
    (canNotes && notes !== baseline.notes) || tagsDirty;
  const toggleTag = (word: string) => {
    setSaved(false);
    setTags((cur) =>
      cur.some((t) => normTag(t) === normTag(word))
        ? cur.filter((t) => normTag(t) !== normTag(word))
        : [...cur, word],
    );
  };
  /**
   * A mark (TWL Q or another outcome) was recorded: the server's stored tags
   * become the baseline, and changes the provider staged but has not saved
   * are kept on top of them.
   */
  const onMarked = (result: TwlQResult) => {
    if (result.provider_tags) {
      const server = result.provider_tags;
      const added = tags.filter((t) => !baseline.tags.some((b) => normTag(b) === normTag(t)));
      const removed = baseline.tags.filter((b) => !tags.some((t) => normTag(t) === normTag(b)));
      const kept = server.filter((t) => !removed.some((r) => normTag(r) === normTag(t)));
      setBaseline((b) => ({ ...b, tags: server }));
      setTags([...kept, ...added.filter((a) => !kept.some((k) => normTag(k) === normTag(a)))]);
    }
    if (onRefresh) onRefresh();
  };
  const dirty = manifestDirty || requestDraft;
  useEffect(() => {
    if (!dirty) return;
    const prevent = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", prevent);
    return () => window.removeEventListener("beforeunload", prevent);
  }, [dirty]);
  const close = () => {
    if (
      !busy &&
      (!dirty ||
        window.confirm(
          manifestDirty
            ? "Discard your unsaved manifest changes?"
            : "Discard the replacement request you have not sent?",
        ))
    )
      onClose();
  };
  const requestForm = canRequest ? (
    <ReplacementRequest
      facilityId={sheet.facility.id}
      patient={patient}
      offline={offline}
      onBusy={setRequesting}
      onDraft={setRequestDraft}
      onRecorded={onRefresh}
    />
  ) : null;
  // The persisted replacement (2026-10-23) when the day sheet told it: the form is offered inside it only when the
  // server would record a request. A payload without it keeps the form as before (the server still refuses a duplicate).
  const replacementStatus = replacementOf(patient);
  const replacement = replacementStatus ? (
    <ReplacementSection
      status={replacementStatus}
      patientId={patient.id}
      offerRequest={canRequest}
    >
      {requestForm}
    </ReplacementSection>
  ) : (
    requestForm
  );
  return (
    <Sheet
      open
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <SheetContent
        className="fw-drawer"
        onInteractOutside={(e) => {
          if (busy || dirty) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (busy) e.preventDefault();
        }}
      >
        <SheetHeader>
          <span className="fw-eyebrow">Resident workbench</span>
          <SheetTitle>{patientName(patient)}</SheetTitle>
          <SheetDescription>
            Room {patient.room_number || "not recorded"} · {sheet.facility.name}
            {patient.room_edited && (
              <>
                {" "}
                <RoomEditedMark monday={patient.room_monday} />
              </>
            )}
          </SheetDescription>
          <WorkbenchSeen patient={patient} />
        </SheetHeader>
        <div className="fw-form">
          {!patient.care_assigned ? (
            <>
              <Notice>{chartLockWords(patient.chart_lock)}</Notice>
              {/* Office readers (PA managers, manifest reviewers, admins) see notes on a chart-locked row. Anyone else is
                  not asked for them at all: the day sheet says so (provenance.privateNotes.officeRead), and the server
                  still decides every read. */}
              {(sheet.provenance as { privateNotes?: { officeRead?: boolean } }).privateNotes?.officeRead === true && (
                <div data-testid="private-notes-slot">
                  <PrivateNotes facilityId={sheet.facility.id} patientId={patient.id} canWrite={false} />
                  <ProviderResidentRecord facilityId={sheet.facility.id} patientId={patient.id} canUpload={false} />
                </div>
              )}
              {replacement}
            </>
          ) : (
            <>
              <div className="fw-chips">
                <StatusBadge tone="green">
                  <ShieldCheck />{" "}
                  {patient.care_basis === "FACILITY_COVERAGE"
                    ? "You cover this building"
                    : "Care-assigned"}
                </StatusBadge>
                <StatusBadge>
                  {patient.testing_status
                    ? TESTING_STATUS_LABEL[patient.testing_status]
                    : "Testing status not recorded"}
                </StatusBadge>
              </div>
              <RemindPaFor facilityId={sheet.facility.id} patientId={patient.id} residentName={patientName(patient)} />
              <Notice>
                These are manifest notes and provider tags. Saving does not sign
                a clinical encounter. Only TWL Q sends the resident to a PA, and
                it asks you first.
              </Notice>
              {patient.alerts && (
                <div className="fw-readonly">
                  <strong>Clinical alert · read only</strong>
                  <p>{patient.alerts}</p>
                </div>
              )}
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (busy || !manifestDirty || offline) return;
                  const patch: ManifestPatch = {};
                  if (canNotes && notes !== baseline.notes)
                    patch.notes = notes.trim() || null;
                  if (tagsDirty) patch.provider_tags = [...tags];
                  setBusy(true);
                  setError("");
                  setSaved(false);
                  try {
                    const message = await onSave(patient.id, patch);
                    if (message) setError(message);
                    else {
                      setBaseline({ notes, tags });
                      setSaved(true);
                      offerSeen(patient.id);
                    }
                  } catch {
                    setError(
                      "Could not reach the server. Your edits remain here; try again.",
                    );
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <label htmlFor="fw-note">
                  Manifest notes {!canNotes && "· read only"}
                </label>
                <Textarea
                  id="fw-note"
                  rows={10}
                  value={notes}
                  readOnly={!canNotes || busy}
                  onChange={(e) => {
                    setNotes(e.target.value);
                    setSaved(false);
                  }}
                />
                {/* Private notes (2026-10-21): providers and the office only, never the facility. */}
                <div data-testid="private-notes-slot">
                  <PrivateNotes facilityId={sheet.facility.id} patientId={patient.id} canWrite={patient.care_assigned} />
                  <ProviderResidentRecord facilityId={sheet.facility.id} patientId={patient.id} canUpload={patient.care_assigned} />
                </div>
                {/* Podiatry encounters (2026-10-27): renders nothing unless the server says this provider has Podiatry here. */}
                <ClinicalEncountersPanel facilityId={sheet.facility.id} patientId={patient.id} patientName={patientName(patient)} facilityName={sheet.facility.name} />
                <TwlQOutcomes
                  vocabulary={vocabulary}
                  preview={canTags ? sheet.provenance.twlQ : undefined}
                  today={sheet.provenance.twlQ?.today?.[patient.id] || null}
                  residentName={patientName(patient)}
                  facilityName={sheet.facility.name}
                  facilityId={sheet.facility.id}
                  patientId={patient.id}
                  offline={offline}
                  disabled={!canTags || saving || requesting || tagsDirty}
                  onBusy={setMarking}
                  onMarked={onMarked}
                />
                {canTags && tagsDirty && (
                  <p className="fw-muted">
                    Save or undo your tag changes before marking TWL Q.
                  </p>
                )}
                <div className="fw-tags-block" role="group" aria-labelledby="fw-tags-title" aria-describedby="fw-tags-help">
                  <h3 className="fw-replacement-title" id="fw-tags-title">
                    Provider tags {!canTags && "· read only"}
                  </h3>
                  <p id="fw-tags-help" className="fw-muted">
                    Tap a tag to turn it on or off, then save. Testing status
                    remains with its authoritative source.
                  </p>
                  <TagChipGroups
                    vocabulary={vocabulary}
                    tags={tags}
                    onToggle={toggleTag}
                    disabled={!canTags || busy}
                  />
                </div>
                {!(canNotes || canTags) && (
                  <p className="fw-muted">
                    Editing unavailable: {sheet.provenance.writeBlockedBy}.
                  </p>
                )}
                {error && (
                  <p role="alert" className="fw-error">
                    {error}
                  </p>
                )}
                <p role="status" className="fw-save-status">
                  {saving
                    ? "Saving to eMerge…"
                    : saved
                      ? "Saved to eMerge."
                      : manifestDirty
                        ? "Unsaved changes · keep this workbench open."
                        : ""}
                </p>
                <div className="fw-actions">
                  <Button
                    className="fw-primary"
                    type="submit"
                    disabled={
                      busy || !manifestDirty || offline || !(canNotes || canTags)
                    }
                  >
                    {saving ? "Saving…" : "Save manifest changes"}
                  </Button>
                  <Button
                    variant="outline"
                    type="button"
                    disabled={busy}
                    onClick={close}
                  >
                    Close
                  </Button>
                </div>
              </form>
              {replacement}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * The whole building, not just this page (2026-10-21, Cindy Aragon's missing residents). The box above filters the
 * loaded page at once; this asks the server about every resident confirmed at the building and, for anyone not on
 * this page, says where they are: "Found in <worklist>: show it", or another page of this worklist. One click opens
 * that worklist at that page with the search kept, so the row is on screen.
 */
function BuildingSearchHint({
  sheet,
  query,
  onPage,
  onShow,
}: {
  sheet: DaySheet;
  query: string;
  onPage: (offset: number) => void;
  onShow: (lens: RosterLens, offset: number) => void;
}) {
  const q = query.trim();
  const roster = sheet.roster;
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "searching" }
    | { kind: "done"; q: string; matches: BuildingMatch[]; more: boolean }
    | { kind: "failed" }
  >({ kind: "idle" });
  useEffect(() => {
    if (q.replace(/\s/g, "").length < 2) {
      setState({ kind: "idle" });
      return undefined;
    }
    let live = true;
    setState({ kind: "searching" });
    const t = window.setTimeout(async () => {
      try {
        const r = await residentSearchApi.search(sheet.facility.id, q, {
          lens: roster.lens,
          order: roster.order,
          limit: roster.limit,
        });
        if (!live) return;
        if (r.success && r.data)
          setState({ kind: "done", q, matches: r.data.matches, more: r.data.more });
        else setState({ kind: "failed" });
      } catch {
        if (live) setState({ kind: "failed" });
      }
    }, 300);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [q, sheet.facility.id, roster.lens, roster.order, roster.limit]);

  if (state.kind === "idle") return null;
  if (state.kind === "searching")
    return (
      <p role="status" className="fw-muted">
        Searching the whole building…
      </p>
    );
  if (state.kind === "failed")
    return (
      <p role="status" className="fw-muted">
        Could not search the whole building just now; this page is still filtered.
      </p>
    );
  const onPageIds = new Set(roster.rows.map((r) => r.id));
  const elsewhere = state.matches.filter(
    (m) => !(m.in_lens && onPageIds.has(m.id)),
  );
  if (!state.matches.length)
    return (
      <p role="status" className="fw-muted">
        No resident confirmed at this building matches “{state.q}”.
      </p>
    );
  if (!elsewhere.length) return null;
  return (
    <div role="status" className="fw-search-elsewhere" aria-label="Found elsewhere in this building">
      <ul>
        {elsewhere.slice(0, 5).map((m) => {
          const name = patientName(m);
          const where = m.show
            ? m.show.lens === roster.lens
              ? "On another page of this worklist"
              : `Found in ${worklistLabel(m.show.lens)}`
            : null;
          return (
            <li key={m.id}>
              <span>
                {name} · Room {m.room_number || "not recorded"}
              </span>{" "}
              {m.show && where ? (
                <Button
                  variant="outline"
                  type="button"
                  aria-label={`${where}: show ${name}`}
                  onClick={() => {
                    const show = m.show;
                    if (!show) return;
                    if (show.lens === roster.lens) onPage(show.offset);
                    else onShow(show.lens as RosterLens, show.offset);
                  }}
                >
                  {where}: show it
                </Button>
              ) : (
                <span className="fw-muted">
                  Status “{m.status || "none"}” is on no worklist. Tell the office.
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {(state.more || elsewhere.length > 5) && (
        <p className="fw-muted">More residents match; type more of the name.</p>
      )}
    </div>
  );
}

export default function Manifest({
  sheet,
  onLens,
  onOrder,
  onPage,
  onSave,
  onRefresh,
  offline,
  loading,
}: Pick<
  FieldWorkspaceProps,
  "sheet" | "onLens" | "onOrder" | "onPage" | "onSave" | "loading"
> & { offline: boolean; onRefresh?: FieldWorkspaceProps["onRefresh"] }) {
  const [search, setSearch] = useState("");
  // The Replacement filter (2026-10-23): a client-side filter of the loaded page only, never a server lens.
  const [replFilter, setReplFilter] = useState<ReplacementFilter>("all");
  const [selected, setSelected] = useState<string | null>(null);
  // Rooms saved here since the day sheet loaded (20261022a); a newer day sheet carries them itself.
  const [roomEdits, setRoomEdits] = useState<Record<string, RoomView>>({});
  useEffect(() => setRoomEdits({}), [sheet]);
  // Facility rounds: today's seen marks for this worklist (FacilityRounds.tsx). "Next resident" pages, and clears the
  // search and Replacement filter when they hide the resident it goes to.
  const rounds = useRounds({ sheet, offline, onPage, onRevealNeeded: () => { setSearch(""); setReplFilter("all"); return true; } });
  if (!sheet) return null;
  const roster = sheet.roster;
  // Facility rounds: a narrowed view of a multi-page worklist lists the whole worklist (FacilityRounds.tsx listRows).
  const rows = rounds.listRows(roster.rows).map((p) => (roomEdits[p.id] ? { ...p, ...roomEdits[p.id] } : p));
  // The room box is offered where the server said it would accept the save: `room` in writableFields, on the rows it
  // marked `room_editable` (rulings 2026-10-22: a provider who works the resident, a PA holding an assignment to them,
  // or the office).
  const canRoom = sheet.provenance.writable && (sheet.provenance.writableFields || []).includes("room");
  const onRoomSaved = (id: string, room: RoomView) => setRoomEdits((cur) => ({ ...cur, [id]: room }));
  // The status box (20261022d): where the server said it would accept the save -- `status` in writableFields, and the
  // row's own `status_editable` (a provider's own resident, or every row for the office).
  const canStatus = (sheet.provenance.writableFields || []).includes("status");
  const statusEdit = sheet.provenance.statusEdit;
  // Offered only when the day sheet carries replacement status on this page's rows.
  const replacementKnown = rows.some((p) => replacementOf(p) !== undefined);
  const activeReplFilter: ReplacementFilter = replacementKnown ? replFilter : "all";
  const filtered = rounds.arrange(rows.filter(
    (p) =>
      `${patientName(p)} ${p.room_number || ""}`
        .toLowerCase()
        .includes(search.toLowerCase().trim()) &&
      matchesReplacementFilter(replacementOf(p), activeReplFilter) &&
      rounds.matches(p),
  ));
  const roundsNarrowed = Boolean(rounds.round) && (rounds.filter !== "all" || rounds.activeCount > 0);
  const narrowed = Boolean(search) || activeReplFilter !== "all" || roundsNarrowed;
  const active = selected
    ? rows.find((p) => p.id === selected)
    : undefined;
  return (
    <RoundsContext.Provider value={rounds}>
      <Section
        title="Facility manifest"
        subtitle="Your building coverage or a care assignment opens a resident's chart. Census and current manifest remain distinct."
      >
        <div className="fw-manifest-tools">
          <div className="fw-search">
            <Search />
            <Input
              aria-label="Search this building by name or room"
              placeholder="Search this building by name or room…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {/* 2026-10-28 (docs/provider/CE_PULL.md): eMerge first, then CounselEAR; shown only where the server offers it. */}
          <CePull
            facilityId={sheet.facility.id}
            facilityName={sheet.facility.name}
            offer={sheet.provenance.cePull}
            offline={offline}
            onShow={(patientId, lens, offset) => {
              setSearch("");
              setReplFilter("all");
              if (lens !== roster.lens) onLens(lens as RosterLens);
              // The same page: read it again, so a resident added a moment ago is on it.
              else if (offset === roster.offset && onRefresh) onRefresh();
              onPage(offset);
              setSelected(patientId);
            }}
          />
          <div className="fw-filter-row">
            <div className="fw-field">
              <span>Worklist</span>
              <FieldSelect
                label="Worklist"
                value={roster.lens}
                disabled={loading}
                onChange={(v) => {
                  setSearch("");
                  setSelected(null);
                  onLens(v as RosterLens);
                }}
                options={WORKLIST_LENSES.map((l) => ({
                  value: l.key,
                  label: `${l.label} · ${(roster.lenses as Record<string, number> | undefined)?.[l.key] ?? "—"}`,
                }))}
              />
            </div>
            <div className="fw-field">
              <span>Walk order</span>
              <FieldSelect
                label="Walk order"
                value={rounds.priorityFirst ? "priority" : roster.order}
                disabled={loading}
                onChange={(v) => {
                  setSelected(null);
                  // "Priority first" (facility rounds): priority residents on top, then the room order.
                  rounds.setPriorityFirst(v === "priority");
                  const o = (v === "priority" ? "room" : v) as RosterOrder;
                  if (o !== roster.order) onOrder(o);
                }}
                options={[
                  { value: "room", label: "Room" },
                  { value: "name", label: "Name" },
                  ...(rounds.priorityReady ? [{ value: "priority", label: "Priority first" }] : []),
                ]}
              />
            </div>
            {replacementKnown && (
              <div className="fw-field">
                <span>Replacement</span>
                <FieldSelect
                  label="Replacement filter, this page only"
                  value={activeReplFilter}
                  onChange={(v) => setReplFilter((v || "all") as ReplacementFilter)}
                  options={REPLACEMENT_FILTERS}
                />
              </div>
            )}
          </div>
          <RoundsHeader rounds={rounds} sheet={sheet} shown={filtered.length} onOpenResident={(id) => { rounds.setFilter("seen"); setSelected(id); }} />
          {activeReplFilter !== "all" && (
            <p className="fw-muted" role="status">
              Replacement filter: {filtered.length} of the {rows.length} residents on this page. Other pages are not
              searched.
            </p>
          )}
          <p className="fw-muted">
            {WORKLIST_LENSES.find((l) => l.key === roster.lens)?.what}
          </p>
          <BuildingSearchHint
            sheet={sheet}
            query={search}
            onPage={onPage}
            onShow={(lens, offset) => {
              setSelected(null);
              onLens(lens);
              onPage(offset);
            }}
          />
          <div className="fw-chips" aria-label="Status legend">
            <StatusBadge tone="green">Qualified</StatusBadge>
            <StatusBadge tone="amber">Verify first</StatusBadge>
            <StatusBadge tone="rose">Do not test</StatusBadge>
            <StatusBadge>Not recorded</StatusBadge>
            <SeenLegend rounds={rounds} />
          </div>
        </div>
        <ul className="fw-residents">
          {filtered.map((p) => {
            const tone = testingTone(p.testing_status);
            const repl = replacementOf(p);
            // Chart locked: "Request replacement" when the server would record one; otherwise, when there is a
            // replacement to see (or the caller may ask here), the workbench opens on its Replacement section.
            const askHere = !p.care_assigned && mayRequestReplacement(sheet, p);
            const ask = askHere && replacementRequestable(repl);
            const viewReplacement =
              !p.care_assigned && !ask && repl !== undefined && (askHere || repl.state !== "NOT_REQUESTED");
            return (
              <li key={p.id} className={rounds.rowClass(p.id)} data-rounds-row={p.id} tabIndex={-1}>
                <div className="fw-avatar" aria-hidden="true">
                  {[p.first_name?.[0], p.last_name?.[0]].join("") || "—"}
                </div>
                <div className="fw-resident-info">
                  <strong>{patientName(p)}</strong>
                  <p>
                    <RoomField
                      facilityId={sheet.facility.id}
                      patient={p}
                      editable={canRoom && p.room_editable === true}
                      offline={offline}
                      onSaved={onRoomSaved}
                    />{" "}
                    ·{" "}
                    <StatusField
                      facilityId={sheet.facility.id}
                      patient={p}
                      editable={canStatus && p.status_editable === true}
                      offline={offline}
                      reasons={statusEdit?.reasons}
                      noteMax={statusEdit?.noteMax}
                    />
                  </p>
                  <div className="fw-chips">
                    <span
                      title={testingTitle(p)}
                      className={`fw-badge ${tone === "ok" ? "green" : tone === "attn" ? "amber" : tone === "stop" ? "rose" : ""}`}
                    >
                      {p.testing_status
                        ? TESTING_STATUS_LABEL[p.testing_status]
                        : "Testing not recorded"}
                    </span>
                    <SeenChip patient={p} rounds={rounds} />
                    {p.annual_due && (
                      <StatusBadge tone="amber">Annual due</StatusBadge>
                    )}
                    {p.enrolled && (
                      <StatusBadge tone="indigo">PA enrolled</StatusBadge>
                    )}
                    {p.testing_status_drift && (
                      <StatusBadge tone="amber">Monday differs</StatusBadge>
                    )}
                    {p.manifest_exclusion && (
                      <StatusBadge>
                        Off current manifest · screened DNQ
                      </StatusBadge>
                    )}
                    <AddedBackMark patient={p} rounds={rounds} />
                    <ReplacementChip status={repl} />
                  </div>
                  {!p.care_assigned && (
                    <p className="fw-muted">
                      <LockKeyhole size={12} /> {chartLockWords(p.chart_lock)}
                    </p>
                  )}
                </div>
                {ask ? (
                  // Chart locked, but the building's providers may still ask for a replacement (summary tier).
                  <Button
                    variant="outline"
                    disabled={loading}
                    onClick={() => setSelected(p.id)}
                    aria-label={`Request replacement for ${patientName(p)}`}
                  >
                    <LockKeyhole />
                    Request replacement
                  </Button>
                ) : viewReplacement ? (
                  // Chart locked; the replacement is said at the summary tier, so it can be read.
                  <Button
                    variant="outline"
                    disabled={loading}
                    onClick={() => setSelected(p.id)}
                    aria-label={`View replacement for ${patientName(p)}`}
                  >
                    <History />
                    Replacement status
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    disabled={!p.care_assigned || loading}
                    onClick={() => setSelected(p.id)}
                    aria-label={`Open workbench for ${patientName(p)}`}
                  >
                    <FilePenLine />
                    {p.care_assigned ? "Open workbench" : "Chart locked"}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
        {!filtered.length && (
          <div className="fw-empty">
            <Search />
            <h3>
              {roundsNarrowed && !search && activeReplFilter === "all" && rows.length
                ? rounds.filter === "remaining" ? "Everyone on this page is seen" : "Nobody on this page is seen yet"
                : narrowed
                  ? "No match on this page"
                  : "No residents in this worklist"}
            </h3>
            <p>
              {search
                ? "Try another name or room, or move to another page."
                : activeReplFilter !== "all"
                  ? "No resident on this page matches the Replacement filter. Other pages may."
                  : roundsNarrowed && rows.length
                    ? "Choose All to see every resident on this page."
                    : narrowed
                      ? "No resident on this page matches. Other pages may."
                      : "Choose another worklist to see the facility census."}
            </p>
            {roundsNarrowed && rows.length > 0 && (
              <Button variant="outline" onClick={() => rounds.setFilter("all")}>
                Show all residents
              </Button>
            )}
            {search && (
              <Button variant="outline" onClick={() => setSearch("")}>
                Clear search
              </Button>
            )}
            {activeReplFilter !== "all" && (
              <Button variant="outline" onClick={() => setReplFilter("all")}>
                Clear Replacement filter
              </Button>
            )}
          </div>
        )}
        <div className="fw-pagination">
          <span>
            {rounds.whole.loaded
              ? `Showing ${filtered.length} of ${roster.total} · the whole worklist`
              : roster.total
                ? `${roster.offset + 1}–${Math.min(roster.offset + roster.returned, roster.total)} of ${roster.total}`
                : "0 residents"}
            {narrowed && !rounds.whole.loaded ? ` · ${filtered.length} matches on this page` : ""}
          </span>
          <div>
            <Button
              variant="outline"
              disabled={loading || roster.offset === 0 || rounds.whole.loaded}
              onClick={() => {
                setSearch("");
                onPage(Math.max(0, roster.offset - roster.limit));
              }}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              disabled={
                loading || roster.offset + roster.returned >= roster.total || rounds.whole.loaded
              }
              onClick={() => {
                setSearch("");
                onPage(roster.offset + roster.limit);
              }}
            >
              Next
            </Button>
          </div>
        </div>
      </Section>
      {active && (
        <ResidentWorkbench
          key={active.id}
          patient={active}
          sheet={sheet}
          onSave={onSave}
          onRefresh={onRefresh}
          onClose={() => setSelected(null)}
          offline={offline}
        />
      )}
    </RoundsContext.Provider>
  );
}
